import { t } from "i18next";
import {
  ValidationError,
  ValidationErrorType,
} from "../types/forms/ValidationResult";
import {
  SimpleFormQuestion,
  SimpleFormValidationError,
} from "../types/dtos/simple-forms";
import { advancedTaskHelper } from "./advancedTaskHelper";

const replaceToken = (
  input: string,
  replaceValue: number | string,
  token: string
): string => {
  let output = input;
  if (input && input.length > 0) {
    output = output.replace(token, replaceValue.toString());
  }
  return output;
};

const replaceMinToken = (input: string, replaceValue: number): string => {
  return replaceToken(input, replaceValue, "#MIN#");
};

const replaceMaxToken = (input: string, replaceValue: number): string => {
  return replaceToken(input, replaceValue, "#MAX#");
};

const replaceCurrentToken = (input: string, replaceValue: number): string => {
  return replaceToken(input, replaceValue, "#CURRENT#");
};

const replaceExpectedValueToken = (
  input: string,
  replaceValue: string
): string => {
  return replaceToken(input, replaceValue, "#EXPECTED_VALUE#");
};

const replaceTaskTypeToken = (
  input: string,
  error: ValidationError
): string => {
  if (!error.taskRestriction) {
    return input;
  }

  // Determine whether to use singular or plural based on the error type and count
  // For category errors, use category min/max; otherwise use top-level min/max
  const minValue = error.taskRestriction.categoryMin !== undefined
    ? error.taskRestriction.categoryMin
    : error.min;
  const maxValue = error.taskRestriction.categoryMax !== undefined
    ? error.taskRestriction.categoryMax
    : error.max;

  let useSingular = false;
  
  switch (error.taskRestriction.failureReason) {
    case "NO-TASKS":
      // Always plural for "no tasks"
      useSingular = false;
      break;
    case "BELOW-MIN-OVERALL":
    case "BELOW-MIN-CATEGORY":
      // Use singular if min is 1, plural otherwise
      useSingular = minValue === 1;
      break;
    case "ABOVE-MAX-OVERALL":
    case "ABOVE-MAX-CATEGORY":
      // Always plural for max errors (max is typically > 1)
      useSingular = false;
      break;
  }
  
  let translationKeyIdentifier = useSingular
    ? error.taskRestriction.taskTypeSingularTranslationKeyIdentifier
    : error.taskRestriction.taskTypeTranslationKeyIdentifier;

  if (!translationKeyIdentifier) {
    translationKeyIdentifier = error.taskRestriction.taskTypeTranslationKeyIdentifier;
  }

  if (!translationKeyIdentifier) {
    return input;
  }
  
  const taskTypeName = advancedTaskHelper.ToLowerCase(translationKeyIdentifier);
  return replaceToken(input, taskTypeName, "#TASK_TYPE#");
};

export const formValidationHelper = {
  /** Get the validation message with numeric values (e.g. for min and max) substituted in where necessary */
  getMessageText: (error: ValidationError): string => {
    if (error.errorType === "CUSTOM" && error.customMessage) {
      return error.customMessage;
    }

    const subTypeText =
      error.subType && error.subType.length > 0 ? `.${error.subType}.` : ".";
    let translationKey = "Common.Validation.Errors" + subTypeText;
    switch (error.errorType) {
      case "MAX":
        translationKey += "Max";
        break;
      case "MIN":
        translationKey += "Min";
        break;
      case "MIN-AND-MAX":
        translationKey += "MinAndMax";
        break;
      case "SELECT-EXACT":
        translationKey += "SelectExact";
        break;
      case "REQUIRED":
        translationKey += "Required";
        break;
      case "TOO-LONG":
        translationKey += "TooLong";
        break;
      case "INVALID-HEX":
        translationKey += "InvalidHex";
        break;
      case "INVALID-DATE":
        translationKey += "InvalidDate";
        break;
      case "ACCESSIBILITY-ISSUE":
        translationKey += "NotAvailableDueToAccessibility";
        break;
      case "NON-UNIQUE_APP_LEVEL_OVERRIDE":
        translationKey += "NonUniqueAppLevelOverride";
        break;
      case "NUMBER-MISMATCH":
        translationKey += "NumberMismatch";
        break;
      case "ATLEAST-ONE-SECTION":
        translationKey += "AtleastOneSection";
        break;
      case "ILLEGAL-CHARACTER":
        translationKey += "IllegalCharacters";
        break;
      case "CUSTOM-TEXT-REQUIRED":
        translationKey += "CustomTextRequired";
        break;
      case "TASKS-REMAIN-OPEN":
        translationKey += "TasksRemainOpen";
        break;
      case "FAILED-TASK-RESTRICTIONS":
        // Use more specific error messages based on the failure reason
        if (error.taskRestriction) {
          switch (error.taskRestriction.failureReason) {
            case "NO-TASKS":
              translationKey += "TaskRestrictionsNotMet.NoTasks";
              break;
            case "BELOW-MIN-OVERALL":
              translationKey += "TaskRestrictionsNotMet.BelowMinOverall";
              break;
            case "ABOVE-MAX-OVERALL":
              translationKey += "TaskRestrictionsNotMet.AboveMaxOverall";
              break;
            case "BELOW-MIN-CATEGORY":
              translationKey += "TaskRestrictionsNotMet.BelowMinCategory";
              break;
            case "ABOVE-MAX-CATEGORY":
              translationKey += "TaskRestrictionsNotMet.AboveMaxCategory";
              break;
          }
        } else {
          translationKey += "TaskRestrictionsNotMet.Generic";
        }
        break;
      case "FAILED-ENFORCED-TASK-COMMENT":
        translationKey += "FailedEnforcedTaskComment";
        break;
      case "TASK-REQUIRES-REVIEW-FORM-RESPONSE":
        translationKey += "TaskRequiresReviewFormResponse";
        break;
      case "CUSTOM-TEXT-FOR-SELECTED-OPTION-REQUIRED":
        translationKey += "CustomTextForSelectedOptionRequired";
        break;
      case "OVERDUE-TASKS":
        translationKey += "OverdueTasks";
        break;
      case "EXPECTED-SUBMIT-VALUE-NOT-SET":
        translationKey += "ExpectedSubmitValueNotSet.";
        translationKey += error.expectedValue ? "WithToken" : "WithoutToken";
        break;
    }

    // Translate the value
    let output = t(translationKey);

    // Replace tokens
    // For category-specific task restriction errors, use category min/max if available
    // Otherwise use the top-level min/max
    const minValue = error.taskRestriction?.categoryMin !== undefined
      ? error.taskRestriction.categoryMin
      : error.min;
    const maxValue = error.taskRestriction?.categoryMax !== undefined
      ? error.taskRestriction.categoryMax
      : error.max;

    if (minValue !== undefined && minValue !== null) {
      output = replaceMinToken(output, minValue);
    }

    if (maxValue !== undefined && maxValue !== null) {
      output = replaceMaxToken(output, maxValue);
    }

    if (error.max && error.current) {
      output = replaceMaxToken(output, error.max);
      output = replaceCurrentToken(output, error.current - error.max);
    }

    if (error.expectedValue) {
      const expectedValueText = error.expectedValue.translate
        ? t(error.expectedValue.value)
        : error.expectedValue.value;
      output = replaceExpectedValueToken(output, expectedValueText);
    }

    // Replace task type token if present (for FAILED-TASK-RESTRICTIONS errors)
    if (error.taskRestriction) {
      output = replaceTaskTypeToken(output, error);
      
      // Replace category name token if present (for category-specific failures)
      if (error.taskRestriction.failingCategoryName) {
        output = output.replace("#CATEGORY_NAME#", t(error.taskRestriction.failingCategoryName));
      }
    }

    return output;
  },
  /** Get instructions for the user on how many items they should select */
  getMultiChoiceMinMaxSelectionMessage: (
    min: number | null,
    max: number | null
  ): string | null => {
    if (min === null && max === null) return null;
    if (min === 0) return null;

    let output = "";
    if (min === null && max !== null) {
      output = t("Common.Validation.Errors.Max", {
        opts:
          max === 1
            ? t("Common.Option").toLowerCase()
            : t("Common.Options").toLowerCase(),
      });
    } else if (min !== null && max === null) {
      output = t("Common.Validation.Errors.Min", {
        opts:
          min === 1
            ? t("Common.Option").toLowerCase()
            : t("Common.Options").toLowerCase(),
      });
    } else if (min === max) {
      output = t("Common.Validation.Errors.SelectExact", {
        opts:
          min === 1
            ? t("Common.Option").toLowerCase()
            : t("Common.Options").toLowerCase(),
      });
    } else {
      // Both numbers not null
      output = t("Common.Validation.Errors.MinAndMax");
    }

    // Replace tokens
    if (min) {
      output = replaceMinToken(output, min);
    }

    if (max) {
      output = replaceMaxToken(output, max);
    }

    return output;
  },

  convertValidationErrorType: function (
    inputError: SimpleFormValidationError,
    question: SimpleFormQuestion
  ): ValidationError {
    let errorType: ValidationErrorType;
    switch (inputError.errorType) {
      case "FAILED-MIN-VALIDATION":
        errorType = "MIN";
        break;
      case "FAILED-MAX-VALIDATION":
        errorType = "MAX";
        break;
      case "FAILED-TO-PROVIDE-CUSTOM-TEXT":
        errorType = "CUSTOM-TEXT-FOR-SELECTED-OPTION-REQUIRED";
        break;
      case "FAILED-INVALID-NUMBER-VALIDATION":
        errorType = "NUMBER-MISMATCH";
        break;
      case "FAILED-REQUIRED-VALIDATION":
      default:
        errorType = "REQUIRED";
        break;
    }

    return {
      errorType: errorType,
      subType: undefined, // Not used in this context
      min: question.validationMin ? question.validationMin : undefined,
      max: question.validationMax ? question.validationMax : undefined,
      current: undefined, // Not used in this context
    };
  },
};

export default formValidationHelper;
